// RecipientPicker — `.ra-picker` popover anchored to the recipient bar.
// design ref: styles.css:2563–2624, bulk-request.jsx:405–478
//
// Search box + scrollable list of people. Clicking a row toggles selection;
// selected rows render with the azurite tint and a check on the right.

import { Box, Flex, Icon, Input, VStack } from "@chakra-ui/react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TbCheck, TbSearch } from "react-icons/tb";
import Avatar from "components/ds/Avatar";
import GroupAvatar from "components/ds/GroupAvatar";
import type { BasketRecipient } from "./BasketDrawer";

export interface PickerPerson extends BasketRecipient {
  /** Optional "Frontend Engineer · Engineering" subtitle. */
  subtitle?: string;
}

interface RecipientPickerProps {
  people: PickerPerson[];
  /** Currently-selected recipient ids — rows are visually checked. */
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
}

const RecipientPicker = ({
  people,
  selectedIds,
  onToggle,
  onClose,
}: RecipientPickerProps) => {
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Origin-aware scale-in: render with the closed style, then flip to the
  // open style on the next paint so the CSS transition runs. Anchoring the
  // transform-origin to top-left makes the popover feel attached to its
  // trigger ("+") rather than appearing from the middle of nowhere.
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Dismiss on outside click — anchored absolute inside the recip bar so
  // we just need to listen on `mousedown` and bail when the target lives
  // outside our root.
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!query) return people;
    const q = query.toLowerCase();
    return people.filter((p) => p.name.toLowerCase().includes(q));
  }, [people, query]);

  return (
    <Box
      ref={ref}
      position="absolute"
      top="calc(100% + 8px)"
      left="28px"
      width="340px"
      bg="neutral.0"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="neutral.200"
      borderRadius="component.2xl" // 12px
      boxShadow="overlay"
      zIndex={50}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      // Start barely scaled-down + transparent, settle to fully visible on
      // the next paint. transform-origin anchors the animation to the "+"
      // button so it reads as opening out of the trigger. Never animate
      // from scale(0) — start at 0.97 minimum so the entrance feels
      // physically grounded (Emil rule).
      transformOrigin="top left"
      opacity={mounted ? 1 : 0}
      transform={mounted ? "scale(1)" : "scale(0.97)"}
      transition="opacity 150ms cubic-bezier(0.23, 1, 0.32, 1), transform 150ms cubic-bezier(0.23, 1, 0.32, 1)"
    >
      {/* .ra-picker__head */}
      <Flex
        align="center"
        gap="sm"
        px="md"
        py="10px"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor="neutral.100"
        color="neutral.600"
      >
        <Icon as={TbSearch} boxSize="16px" />
        <Input
          variant="unstyled"
          autoFocus
          flex={1}
          placeholder="Search people…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fontSize="md"
          color="neutral.950"
          _placeholder={{ color: "neutral.500" }}
        />
      </Flex>

      {/* .ra-picker__list */}
      <VStack
        align="stretch"
        maxH="320px"
        overflowY="auto"
        p="1xs"
        spacing="2px"
      >
        {filtered.length === 0 && (
          <Box p="lg" textAlign="center" fontSize="smd" color="neutral.600">
            No matches
          </Box>
        )}
        {filtered.map((p) => {
          const selected = selectedIds.includes(p.id);
          return (
            <Box
              key={p.id}
              as="button"
              type="button"
              onClick={() => onToggle(p.id)}
              display="flex"
              alignItems="center"
              gap="10px"
              p="sm"
              borderRadius="component.lg" // 8px
              bg={selected ? "p.50" : "transparent"}
              border="none"
              cursor="pointer"
              fontFamily="inherit"
              textAlign="left"
              transition="background-color 100ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
              _hover={{ bg: selected ? "p.100" : "neutral.50" }}
              _active={{ transform: "scale(0.98)" }}
            >
              {p.isGroup ? (
                <GroupAvatar count={p.groupCount ?? 0} size="sm" />
              ) : (
                <Avatar
                  name={p.name}
                  src={p.avatarSrc}
                  tone={p.tone}
                  size="sm"
                />
              )}
              <Box display="flex" flexDirection="column" flex={1} minW={0}>
                <Box fontSize="md" fontWeight={500} color="neutral.950">
                  {p.name}
                  {p.suffix && (
                    <Box as="span" color="neutral.600" fontWeight={400} ml="2xs">
                      {p.suffix}
                    </Box>
                  )}
                </Box>
                {p.subtitle && (
                  <Box
                    fontSize="xs"
                    color="neutral.600"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {p.subtitle}
                  </Box>
                )}
              </Box>
              {selected && (
                <Icon as={TbCheck} boxSize="16px" color="p.600" />
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

export default RecipientPicker;
